<script>
  import { onMount } from "svelte";
  import { _ } from "../stores/index";
  import CustomTablePolicies from "./CustomTablePolicies.svelte";
  import {
    Loader,
    Flex,
    Select,
    SelectItem,
    Button,
    Icon,
    FlexItem,
  } from "@gl/design-system";
  import { PoliciesRepository } from "../repositories/index";
  import { parseUrl, setUrl } from "../utils/index";

  export let subPolicies;
  export let disabled;
  export let groupsPolicies;
  export let rolesPolicies;

  let loading = true;

  let policies = [];
  let allObjs = [];
  let allActs = [];
  let implicitPermissions = [];
  let existingApps = [];
  let applicationView = "all";
  let searched;

  let table;

  async function fetchAll() {
    try {
      loading = true;
      const result = await PoliciesRepository.getPoliciesOfContract();

      policies = result.list;

      existingApps = policies.reduce((acc, curr) => {
        if (!acc.some((a) => a.id === curr.appId)) {
          acc.push({ id: curr.appId, name: curr.appName, type: curr.appType });
        }
        return acc;
      }, []);

      for (const policy of policies) {
        if (!allActs.includes(policy.act)) {
          allActs.push(policy.act);
        }

        if (
          !allObjs.some((p) => p.obj === policy.obj && p.appId === policy.appId)
        ) {
          allObjs.push({
            obj: policy.obj,
            acts: {
              [policy.act]: policy.id,
            },
            appName: policy.appName,
            appId: policy.appId,
            appType: policy.appType,
          });
        } else {
          allObjs.map((p) => {
            if (p.obj === policy.obj && p.appId === policy.appId) {
              p.acts = { ...p.acts, [policy.act]: policy.id };
            }
            return p.act;
          });
        }

        // implicit permissions
        if (policy.implicit) {
          implicitPermissions.push({
            parent: policy.id,
            implicit: policy.implicit,
            appId: policy.appId,
          });
        }
      }
    } finally {
      loading = false;
    }
  }

  const handleApplicationsViewChange = () => {
    if (applicationView === "all") {
      searched = allObjs;
    } else {
      searched = allObjs.filter((o) => o.appId === applicationView);
    }
    setUrl({ appView: applicationView === "all" ? null : applicationView });
  };

  export const getSelected = () => {
    return table.getSelected();
  };

  export const reset = () => {
    applicationView = "all";
    searched = allObjs;
  };

  onMount(async () => {
    await fetchAll();
    const query = parseUrl(window.location.search);
    if (query.appView) {
      applicationView = query.appView;
      handleApplicationsViewChange();
    }
  });
</script>

{#if loading}
  <Loader status="info" />
{:else}
  <Flex class="align-items-end">
    <div class="select">
      <Select
        bind:value={applicationView}
        on:change={() => handleApplicationsViewChange()}
      >
        <SelectItem
          label={`${$_("common.apps")}: ${$_("common.all")}`}
          value="all"
        />
        {#each existingApps as app}
          <SelectItem
            label={`${$_("common.apps")}: ${app.name} (${app.type})`}
            value={app.id}
          />
        {/each}
      </Select>
    </div>

    {#if groupsPolicies && rolesPolicies}
      <FlexItem pushEnd>
        <Flex class="align-items-center">
          <p class="text-light mr-2">{$_("common.legend")}:</p>

          <div class="label-circle-group mx-2" />
          <p class="mr-2">{$_("policy.inherit.group")}</p>

          <div class="label-circle-role mx-2" />
          {$_("policy.inherit.role")}
        </Flex>
      </FlexItem>
    {:else if !disabled}
      <FlexItem pushEnd>
        <Flex class="align-items-center">
          <p class="text-light mr-2">{$_("common.legend")}:</p>

          <div class="label-circle-explicit mx-2" />
          <p class="mr-2">{$_("policies.explicit")}</p>

          <div class="label-circle-implicit mx-2" />
          {$_("policies.implicit")}
        </Flex>
      </FlexItem>
    {/if}
  </Flex>

  <CustomTablePolicies
    allObjs={searched || allObjs}
    {allActs}
    {implicitPermissions}
    {disabled}
    {groupsPolicies}
    {rolesPolicies}
    {subPolicies}
    bind:this={table}
  />
{/if}

<style>
  .label-circle-group,
  .label-circle-role,
  .label-circle-explicit,
  .label-circle-implicit {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .label-circle-group,
  .label-circle-explicit {
    background-color: var(--gl-primary);
  }

  .label-circle-role {
    background-color: var(--gl-warning);
  }

  .label-circle-implicit {
    background-color: var(--gl-text-disabled);
  }
</style>
